import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Search, ArrowLeftRight, Users, History, ArrowRight } from "lucide-react";
import AirportAutocomplete from "@/components/site/AirportAutocomplete";
import DatePicker from "@/components/site/DatePicker";
import { recentSearches } from "@/lib/recentSearches";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { format, parseISO, isValid } from "date-fns";

const TODAY = new Date().toISOString().slice(0, 10);

const NUM = (lo, hi) => Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);

export default function FlightSearchWidget({ compact = false, initial = null }) {
  const [tripType, setTripType] = useState(initial?.trip_type || "one_way");
  const [adults, setAdults] = useState(Number(initial?.adults || initial?.passengers || 1));
  const [children, setChildren] = useState(Number(initial?.children || 0));
  const [infants, setInfants] = useState(Number(initial?.infants || 0));
  const [cabin, setCabin] = useState(initial?.cabin || "economy");
  const [direct, setDirect] = useState(!!initial?.direct);
  const [airline, setAirline] = useState(initial?.airline || "");
  const [segments, setSegments] = useState(initial?.segments || [
    { from: "", to: "", date: "" },
  ]);
  const [returnDate, setReturnDate] = useState(initial?.return_date || "");
  // Flexible dates ±3 days — when ON, the results page also fetches a 7-day
  // price strip so the user can shift the outbound date with one click.
  const [flexDates, setFlexDates] = useState(!!initial?.flex_dates);
  const { user } = useAuth();
  const userId = user?.id || null;
  const [recent, setRecent] = useState(() => {
    try { return recentSearches.list(userId); } catch { return []; }
  });
  // Re-load recents whenever the user identity changes (login / logout / switch).
  useEffect(() => {
    try { setRecent(recentSearches.list(userId)); } catch { setRecent([]); }
  }, [userId]);
  const navigate = useNavigate();

  const handleFieldChange = (i, key, val) => {
    const next = [...segments];
    next[i] = { ...next[i], [key]: val };
    setSegments(next);
  };
  const update = handleFieldChange;
  const addSeg = () => setSegments((prev) => [...prev, { from: prev[prev.length - 1].to || "", to: "", date: "" }]);
  const removeSeg = (i) => setSegments((prev) => prev.filter((_, idx) => idx !== i));
  const swapEnds = (i) => setSegments((prev) => {
    const next = [...prev];
    next[i] = { ...next[i], from: next[i].to || "", to: next[i].from || "" };
    return next;
  });
  const changeTripType = (t) => {
    setTripType(t);
    setSegments((prev) => {
      if ((t === "multi_city" || t === "custom") && prev.length < 2) {
        return [prev[0] || { from: "", to: "", date: "" }, { from: "", to: "", date: "" }];
      }
      if (t !== "multi_city" && t !== "custom" && prev.length > 1) {
        return [prev[0]];
      }
      return prev;
    });
  };

  const minSegments = useMemo(() => (tripType === "multi_city" || tripType === "custom" ? Math.max(2, segments.length) : 1), [tripType, segments.length]);

  const validateSegments = () => {
    const visible = tripType === "one_way" || tripType === "round_trip" ? segments.slice(0, 1) : segments;
    for (let i = 0; i < visible.length; i++) {
      const s = visible[i];
      if (!s.from || s.from.length < 3) { toast.error(`Segment ${i + 1}: select an origin airport`); return false; }
      if (!s.to || s.to.length < 3) { toast.error(`Segment ${i + 1}: select a destination airport`); return false; }
      if (s.from.toUpperCase() === s.to.toUpperCase()) { toast.error(`Segment ${i + 1}: origin and destination cannot be the same`); return false; }
      if (!s.date) { toast.error(`Segment ${i + 1}: pick a departure date`); return false; }
      if (s.date < TODAY) { toast.error(`Segment ${i + 1}: departure date cannot be in the past`); return false; }
      // Multi-city / build-your-route: enforce sequential dates so each leg
      // happens on or after the previous one (same rule we already imply via
      // each DatePicker's minDate).
      if (i > 0 && (tripType === "multi_city" || tripType === "custom")) {
        const prev = visible[i - 1];
        if (prev?.date && s.date < prev.date) {
          toast.error(`Segment ${i + 1}: departure must be on or after Segment ${i} (${prev.date})`);
          return false;
        }
      }
    }
    if (tripType === "round_trip") {
      if (!returnDate) { toast.error("Pick a return date"); return false; }
      const outbound = visible[0]?.date;
      if (outbound && returnDate < outbound) {
        toast.error("Return date must be on or after the departure date");
        return false;
      }
    }
    const totalPax = Number(adults) + Number(children) + Number(infants);
    if (totalPax < 1) { toast.error("Add at least one adult traveller"); return false; }
    if (totalPax > 9) { toast.error("Max 9 passengers per booking"); return false; }
    if (Number(infants) > Number(adults)) { toast.error("Infants cannot exceed adults"); return false; }
    return true;
  };

  const submit = (e) => {
    e?.preventDefault();
    if (!validateSegments()) return;
    let segs = [...segments];
    if (tripType === "round_trip") {
      segs = [segments[0], { from: segments[0].to, to: segments[0].from, date: returnDate }];
    }
    const passengers = Number(adults) + Number(children) + Number(infants);
    const payload = { trip_type: tripType, passengers,
                      adults: Number(adults), children: Number(children), infants: Number(infants),
                      cabin, segments: segs, direct, airline: (airline || "").toUpperCase(),
                      flex_dates: !!flexDates };
    recentSearches.push(payload, userId);
    navigate("/flights", { state: payload });
  };

  const totalPax = Number(adults) + Number(children) + Number(infants);
  const paxLabel = `${totalPax} pax · ${cabin.charAt(0).toUpperCase() + cabin.slice(1)}`;
  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = parseISO(iso);
    return isValid(d) ? format(d, "dd/MM/yyyy") : iso;
  };
  const applyRecent = (r) => {
    setTripType(r.trip_type || "one_way");
    setAdults(r.adults || 1); setChildren(r.children || 0); setInfants(r.infants || 0);
    setCabin(r.cabin || "economy");
    setDirect(!!r.direct); setAirline(r.airline || "");
    setSegments(r.segments || [{ from: "", to: "", date: "" }]);
    if (r.return_date) setReturnDate(r.return_date);
    setTimeout(() => navigate("/flights", { state: r }), 100);
  };

  return (
    <form onSubmit={submit} className={compact ? "" : "glass-card rounded-3xl p-5 md:p-7"}>
      <Tabs value={tripType} onValueChange={changeTripType}>
        {/* Horizontal scroll fallback on mobile so all 4 trip types stay in
            the tab strip instead of overflowing/clipping the last one. */}
        <TabsList className="bg-white/60 backdrop-blur h-11 p-1 rounded-full flex w-full max-w-full overflow-x-auto no-scrollbar whitespace-nowrap justify-start sm:justify-center">
          <TabsTrigger value="one_way" data-testid="flight-tab-one-way" className="rounded-full px-3 sm:px-4 text-xs sm:text-sm shrink-0">One-Way</TabsTrigger>
          <TabsTrigger value="round_trip" data-testid="flight-tab-round-trip" className="rounded-full px-3 sm:px-4 text-xs sm:text-sm shrink-0">Round-Trip</TabsTrigger>
          <TabsTrigger value="multi_city" data-testid="flight-tab-multi-city" className="rounded-full px-3 sm:px-4 text-xs sm:text-sm shrink-0">Multi-City</TabsTrigger>
          <TabsTrigger value="custom" data-testid="flight-tab-custom" className="rounded-full px-3 sm:px-4 text-xs sm:text-sm shrink-0">Build-Your-Route</TabsTrigger>
        </TabsList>

        <TabsContent value={tripType} forceMount>
          <div className="mt-5 space-y-3">
            {segments.slice(0, tripType === "one_way" || tripType === "round_trip" ? 1 : segments.length).map((seg, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-white/70 p-3 rounded-2xl border border-slate-200/70">
                {/* From + Swap + To — col-span shrinks on round-trip so the
                    extra "Return" date can sit on the same row. */}
                <div className={`${tripType === "round_trip" ? "md:col-span-6" : "md:col-span-7"} grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end relative`}>
                  <AirportAutocomplete
                    label="From"
                    placeholder="Origin"
                    value={seg.from}
                    onChange={(a) => update(i, "from", a?.code || "")}
                    testId={`flight-from-${i}`}
                  />
                  <div className="hidden md:flex items-end pb-0.5">
                    <button
                      type="button"
                      data-testid={`flight-swap-${i}`}
                      onClick={() => swapEnds(i)}
                      title="Swap origin and destination"
                      aria-label="Swap origin and destination"
                      className="w-9 h-9 rounded-full bg-white border border-teal-200 shadow-sm hover:shadow-md hover:bg-teal-50 hover:border-[var(--brand-primary)] text-[var(--brand-primary-dark)] flex items-center justify-center transition-all hover:rotate-180 duration-300"
                    >
                      <ArrowLeftRight className="w-4 h-4" />
                    </button>
                  </div>
                  <AirportAutocomplete
                    label="To"
                    placeholder="Destination"
                    value={seg.to}
                    onChange={(a) => update(i, "to", a?.code || "")}
                    testId={`flight-to-${i}`}
                  />
                </div>
                <div className={tripType === "round_trip" ? "md:col-span-3" : "md:col-span-3"}>
                  <DatePicker
                    testId={`flight-date-${i}`}
                    label="Departure"
                    placeholder="Pick a date"
                    value={seg.date}
                    minDate={
                      (tripType === "multi_city" || tripType === "custom") && i > 0
                        ? (segments[i - 1]?.date || TODAY)
                        : TODAY
                    }
                    onChange={(v) => update(i, "date", v)}
                  />
                </div>
                {tripType === "round_trip" && i === 0 && (
                  <div className="md:col-span-3">
                    <DatePicker
                      testId="flight-return-date"
                      label="Return"
                      placeholder="Pick a date"
                      value={returnDate}
                      minDate={seg.date || TODAY}
                      onChange={(v) => setReturnDate(v)}
                    />
                  </div>
                )}
                {/* Flexible dates toggle — only for one-way / round-trip outbound
                    (where a 7-day price strip makes sense; multi-city is too
                    cross-product to flex on the home widget). */}
                {(tripType === "one_way" || tripType === "round_trip") && i === 0 && (
                  <label
                    data-testid="flight-flex-dates"
                    className="md:col-span-12 mt-1 inline-flex items-center gap-2 cursor-pointer text-xs text-[var(--brand-accent)] select-none"
                  >
                    <input
                      type="checkbox"
                      checked={flexDates}
                      onChange={(e) => setFlexDates(e.target.checked)}
                      className="accent-[var(--brand-primary)] w-4 h-4"
                    />
                    <span className="font-medium">Flexible dates (± 3 days)</span>
                    <span className="text-slate-500 hidden sm:inline">— compare prices across the surrounding week</span>
                  </label>
                )}
                {(tripType === "multi_city" || tripType === "custom") && segments.length > minSegments && (
                  <div className="md:col-span-3 flex md:justify-end">
                    <Button data-testid={`flight-remove-segment-${i}`} type="button" variant="ghost" onClick={() => removeSeg(i)} className="text-red-500">
                      <Trash2 className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {(tripType === "multi_city" || tripType === "custom") && (
              <div className="flex items-center gap-3">
                <Button
                  data-testid="flight-add-segment"
                  type="button"
                  onClick={addSeg}
                  className="rounded-full bg-[var(--brand-primary)] text-white border border-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] hover:border-[var(--brand-primary-dark)] shadow-sm font-medium"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add segment
                </Button>
                {tripType === "custom" && (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                    Build-Your-Own-Route: segments are at your own risk
                  </span>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 pt-2">
              {/* Travellers + cabin popover (replaces 3 counters) */}
              <div className="md:col-span-3">
                <Label className="text-xs text-slate-500">Travellers & cabin</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="flight-travellers"
                            className="w-full h-11 justify-start font-normal text-left text-[var(--brand-accent)]">
                      <Users className="w-4 h-4 mr-2 text-[var(--brand-primary)]" /> {paxLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start">
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-[11px] text-slate-500">Adults<span className="block text-[9px] text-slate-400">≥ 12y</span></Label>
                          <Select value={String(adults)} onValueChange={(v) => setAdults(Number(v))}>
                            <SelectTrigger data-testid="flight-adults" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{NUM(1, 9).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Children<span className="block text-[9px] text-slate-400">2–11y</span></Label>
                          <Select value={String(children)} onValueChange={(v) => setChildren(Number(v))}>
                            <SelectTrigger data-testid="flight-children" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{NUM(0, 8).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Infants<span className="block text-[9px] text-slate-400">&lt; 2y</span></Label>
                          <Select value={String(infants)} onValueChange={(v) => setInfants(Number(v))}>
                            <SelectTrigger data-testid="flight-infants" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{NUM(0, Number(adults)).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Cabin class</Label>
                        <Select value={cabin} onValueChange={setCabin}>
                          <SelectTrigger data-testid="flight-cabin" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="economy">Economy</SelectItem>
                            <SelectItem value="premium">Premium Economy</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="first">First</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="text-[11px] text-slate-500">Max 9 passengers per booking. Infants ≤ adults.</div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Airline IATA filter */}
              <div className="md:col-span-2">
                <Label className="text-xs text-slate-500">Airline (optional)</Label>
                <Input data-testid="flight-airline" placeholder="e.g. EK, BA" maxLength={3}
                       value={airline} onChange={(e) => setAirline(e.target.value.toUpperCase())} className="h-11 uppercase" />
              </div>

              {/* Direct only */}
              <div className="md:col-span-2 flex items-end">
                <label className="flex items-center gap-2 h-11 px-3 rounded-md border border-slate-200 cursor-pointer hover:border-teal-300 transition-colors w-full">
                  <Checkbox data-testid="flight-direct" checked={direct} onCheckedChange={(v) => setDirect(!!v)} />
                  <span className="text-sm text-[var(--brand-accent)] font-medium">Direct only</span>
                </label>
              </div>

              <div className="md:col-span-5 flex md:justify-end items-end">
                <Button data-testid="flight-search-submit" type="submit" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] h-12 px-8 rounded-full text-base shadow-lg shadow-teal-500/20">
                  <Search className="w-4 h-4 mr-2" /> Search flights
                </Button>
              </div>
            </div>

            {/* Recent searches */}
            {recent.length > 0 && (
              <div data-testid="recent-searches" className="pt-3 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold inline-flex items-center gap-1 mb-2">
                  <History className="w-3 h-3" /> Recent searches
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.map((r, i) => {
                    const s0 = r.segments?.[0] || {};
                    return (
                      <button
                        key={i}
                        type="button"
                        data-testid={`recent-search-${i}`}
                        onClick={() => applyRecent(r)}
                        className="text-xs inline-flex items-center gap-1.5 bg-teal-50 hover:bg-teal-100 text-[var(--brand-primary-dark)] border border-teal-200 rounded-full px-3 py-1.5 transition-colors"
                      >
                        <span className="font-mono font-bold">{s0.from || "?"}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="font-mono font-bold">{s0.to || "?"}</span>
                        <span className="text-teal-500">·</span>
                        <span>{formatDate(s0.date)}</span>
                        <span className="text-teal-500">·</span>
                        <span>{(r.passengers || 1)} pax</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </form>
  );
}
